import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {
  getUnreadMessages,
  getAllMessages,
  markMessageAsRead,
  dismissMessage
} from '../services/APIClient';

export const MessageModal = () => {
  const [message, setMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkForMessages();
  }, [location.pathname]);

  const checkForMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return;
      }

      const currentPath = location.pathname.substring(1);
      const messages = await getUnreadMessages(currentPath);

      if (messages && messages.length > 0) {
        setMessage(messages[0]);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('❌ Failed to fetch messages:', error);
    }
  };

  const handleDismiss = async () => {
    if (!message) return;

    try {
      await dismissMessage(message.id);
      setIsOpen(false);
      setMessage(null);
    } catch (error) {
      console.error('❌ Failed to dismiss message:', error);
      setIsOpen(false);
      setMessage(null);
    }
  };

  const handleMarkAsRead = async () => {
    if (!message) return;

    try {
      await markMessageAsRead(message.id);
      setIsOpen(false);
      setMessage(null);
    } catch (error) {
      console.error('❌ Failed to mark as read:', error);
      setIsOpen(false);
      setMessage(null);
    }
  };

  if (!isOpen || !message) {
    return null;
  }

  const getModalBackground = () => {
    if (message.messageType === 'milestone' || message.messageType === 'achievement') {
      return 'linear-gradient(to bottom right, #f0fdf4, #dcfce7)';
    }
    if (message.priority === 'high') {
      return 'linear-gradient(to bottom right, #fef2f2, #fee2e2)';
    }
    return '#ffffff';
  };

  const getBorderColor = () => {
    if (message.messageType === 'milestone' || message.messageType === 'achievement') {
      return '#86efac';
    }
    if (message.priority === 'high') {
      return '#fca5a5';
    }
    return '#e5e7eb';
  };

  const getIcon = () => {
    if (message.messageType === 'milestone') return '🎯';
    if (message.messageType === 'achievement') return '🏆';
    if (message.priority === 'high') return '⚠️';
    return '📢';
  };

  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    padding: '20px'
  };

  const modalStyle = {
    background: getModalBackground(),
    border: `2px solid ${getBorderColor()}`,
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    position: 'relative',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '20px'
  };

  const iconStyle = {
    fontSize: '48px',
    marginRight: '16px',
    lineHeight: 1
  };

  const titleStyle = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#111827',
    margin: 0,
    flex: 1
  };

  const bodyStyle = {
    color: '#374151',
    marginBottom: '24px',
    lineHeight: '1.6',
    fontSize: '16px'
  };

  const timestampStyle = {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  };

  const dismissButtonStyle = {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#4b5563',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'color 0.2s'
  };

  const gotItButtonStyle = {
    padding: '10px 24px',
    backgroundColor: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.2s'
  };

  const modalContent = (
    <div 
      style={backdropStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleDismiss();
        }
      }}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header with Icon and Title */}
        <div style={headerStyle}>
          <span style={iconStyle}>{getIcon()}</span>
          <h2 style={titleStyle}>{message.title}</h2>
        </div>

        {/* Body */}
        <div 
          style={bodyStyle}
          dangerouslySetInnerHTML={{__html: message.body}}
        />

        {/* Timestamp */}
        <p style={timestampStyle}>
          {new Date(message.createdAt).toLocaleDateString('en-IE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>

        {/* Action Buttons */}
        <div style={buttonContainerStyle}>
          <button
            style={dismissButtonStyle}
            onClick={handleDismiss}
            onMouseEnter={(e) => e.target.style.color = '#1f2937'}
            onMouseLeave={(e) => e.target.style.color = '#4b5563'}
          >
            Dismiss
          </button>
          <button
            style={gotItButtonStyle}
            onClick={handleMarkAsRead}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#15803d'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#16a34a'}
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

MessageModal.propTypes = {};